import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronRight, Sparkles,
  Bath, Bed, Shirt, ChefHat, Sofa, Tv, Wifi, Snowflake, Lock, Trees,
  Car, Baby, Accessibility, Leaf, Dumbbell, Settings
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { amenityLabel, amenityDesc } from "../data/amenitiesI18n";
import { readableText } from "../data/themePresets";

// Category id → translation key (used to label tile + modal)
const CAT_TKEY = {
  bagno: "pl.cat.bagno",
  camera: "pl.cat.camera",
  lavanderia: "pl.cat.lavanderia",
  cucina: "pl.cat.cucina",
  soggiorno: "pl.cat.soggiorno",
  intrattenimento: "pl.cat.intrattenimento",
  internet_ufficio: "pl.cat.internet",
  clima: "pl.cat.climatizzazione",
  sicurezza: "pl.cat.sicurezza",
  esterni: "pl.cat.esterno",
  parcheggio: "pl.cat.parcheggio",
  bambini: "pl.cat.famiglia",
  accessibilita: "pl.cat.accessibilita",
  sostenibilita: "pl.cat.eco",
  fitness: "pl.cat.benessere",
  servizi: "pl.cat.servizi",
  servizi_politiche: "pl.cat.servizi",
};

// Map category id → Lucide icon (modern, tech)
const CATEGORY_ICONS = {
  bagno: Bath,
  camera: Bed,
  lavanderia: Shirt,
  cucina: ChefHat,
  soggiorno: Sofa,
  intrattenimento: Tv,
  internet_ufficio: Wifi,
  clima: Snowflake,
  sicurezza: Lock,
  esterni: Trees,
  parcheggio: Car,
  bambini: Baby,
  accessibilita: Accessibility,
  sostenibilita: Leaf,
  fitness: Dumbbell,
  servizi_politiche: Settings,
};

/**
 * AmenitiesTreemap
 * Treemap-style proportional grid: each category tile sized by item count.
 * Click expands the tile across the row, revealing item list with stagger.
 *
 * Props:
 *  - amenityIds: string[] (selected amenity ids)
 *  - amenitiesData: full AMENITIES_DATA catalog
 *  - theme
 */

// Default vibrant palette rotating per category (modern, tech, App Store-ish)
const DEFAULT_PALETTE = [
  { bg: "#FF385C", text: "#FFFFFF", soft: "#FFE7EC" }, // Airbnb red
  { bg: "#7C3AED", text: "#FFFFFF", soft: "#EDE9FE" }, // Purple
  { bg: "#10B981", text: "#FFFFFF", soft: "#D1FAE5" }, // Emerald
  { bg: "#F59E0B", text: "#FFFFFF", soft: "#FEF3C7" }, // Amber
  { bg: "#3B82F6", text: "#FFFFFF", soft: "#DBEAFE" }, // Blue
  { bg: "#EC4899", text: "#FFFFFF", soft: "#FCE7F3" }, // Pink
  { bg: "#14B8A6", text: "#FFFFFF", soft: "#CCFBF1" }, // Teal
  { bg: "#F97316", text: "#FFFFFF", soft: "#FFEDD5" }, // Orange
];

// Lucide icon rendered in a soft inverted bubble (white over colored tile)
const CategoryIcon = ({ catId, size = 28 }) => {
  const Icon = CATEGORY_ICONS[catId] || Settings;
  return (
    <div
      className="rounded-xl flex items-center justify-center"
      style={{
        width: size + 14,
        height: size + 14,
        background: "rgba(255,255,255,0.22)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.32)"
      }}
    >
      <Icon style={{ width: size, height: size }} strokeWidth={2.1} />
    </div>
  );
};

const AmenitiesTreemap = ({ amenityIds = [], amenitiesData = [], theme, fullWidth = false, paletteColors = null }) => {
  const { t, lang } = useLanguage();
  const [activeCatId, setActiveCatId] = useState(null);
  const PALETTE = useMemo(() => (
    paletteColors && paletteColors.length
      ? paletteColors.map((bg) => ({ bg, text: readableText(bg), soft: "#FFFFFF" }))
      : DEFAULT_PALETTE
  ), [paletteColors]);

  const catLabel = (catId, fallback) => {
    const k = CAT_TKEY[catId];
    return k ? t(k) : (fallback || catId);
  };

  const grouped = useMemo(() => {
    const lookup = new Map();
    amenitiesData.forEach((cat) => {
      cat.items.forEach((item) => lookup.set(item.id, { ...item, catId: cat.id, catLabel: cat.label, catEmoji: cat.emoji }));
    });
    const cats = {};
    amenityIds.forEach((id) => {
      const it = lookup.get(id);
      if (!it) return;
      if (!cats[it.catId]) cats[it.catId] = { id: it.catId, label: it.catLabel, emoji: it.catEmoji, items: [] };
      cats[it.catId].items.push(it);
    });
    return Object.values(cats).sort((a, b) => b.items.length - a.items.length);
  }, [amenityIds, amenitiesData]);

  const maxCount = useMemo(() => Math.max(1, ...grouped.map((g) => g.items.length)), [grouped]);
  const total = useMemo(() => grouped.reduce((s, g) => s + g.items.length, 0), [grouped]);

  if (grouped.length === 0) return null;

  const active = activeCatId ? grouped.find((g) => g.id === activeCatId) : null;

  return (
    <div className="w-full" data-testid="amenities-treemap">
      {/* Header — coherent editorial style w/ POI section */}
      <div className="mb-3 md:mb-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-5 h-5 md:w-6 md:h-6 rounded-md md:rounded-lg flex items-center justify-center" style={{ background: `${theme.text || "#0F172A"}0d` }}>
            <Sparkles className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: theme.text || "#0F172A" }} strokeWidth={2.3} />
          </span>
          <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.sub }}>
            Cosa troverai
            <span className="mx-1.5 opacity-40">·</span>
            <span style={{ color: theme.text || "#0F172A" }}>{total}</span> servizi
          </p>
        </div>
      </div>

      {/* Bar-chart: rows left-aligned, width ∝ item count, descending order */}
      <div className="flex flex-col gap-2 md:gap-2.5">
        {grouped.map((cat, idx) => {
          const color = PALETTE[idx % PALETTE.length];
          const ratio = cat.items.length / maxCount;
          const widthPct = 58 + ratio * 42; // 58%..100%
          return (
            <motion.button
              key={cat.id}
              initial={{ opacity: 0, x: -14 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.04, type: "spring", damping: 20, stiffness: 220 }}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.985 }}
              onClick={() => setActiveCatId(cat.id)}
              className="group relative rounded-2xl md:rounded-3xl overflow-hidden text-left p-3 md:p-3.5 w-full"
              style={{
                maxWidth: `${widthPct}%`,
                background: color.bg,
                color: color.text,
                boxShadow: "0 4px 16px -6px rgba(0,0,0,0.15)"
              }}
              data-testid={`treemap-tile-${cat.id}`}
            >
              {/* Animated geometric pattern overlay */}
              <motion.div
                className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
                style={{ background: "rgba(255,255,255,0.18)" }}
                animate={{ scale: [1, 1.15, 1], rotate: [0, 12, 0] }}
                transition={{ duration: 7 + idx, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -bottom-8 -left-4 w-20 h-20 rounded-full pointer-events-none"
                style={{ background: "rgba(255,255,255,0.1)" }}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 9 + idx, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />

              <div className="relative flex items-center gap-2.5 md:gap-3">
                <div className="shrink-0">
                  <CategoryIcon catId={cat.id} size={fullWidth ? 20 : 18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] md:text-[10px] font-bold uppercase tracking-wider opacity-75 leading-none mb-1">
                    {cat.items.length} {cat.items.length === 1 ? t("pl.amenities.items_one") : t("pl.amenities.items_many")}
                  </p>
                  <p className="text-xs md:text-sm font-bold leading-tight truncate" style={{ letterSpacing: "-0.01em" }}>
                    {catLabel(cat.id, cat.label)}
                  </p>
                </div>
                <ChevronRight className="w-3.5 h-3.5 opacity-70 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Expanded modal overlay */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[80] flex items-end md:items-center justify-center p-0 md:p-8"
            style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }}
            onClick={() => setActiveCatId(null)}
            data-testid="treemap-expanded"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={{ type: "spring", damping: 26, stiffness: 240, mass: 0.7 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full md:max-w-2xl max-h-[88vh] md:max-h-[80vh] overflow-y-auto overflow-x-hidden overscroll-contain rounded-t-3xl md:rounded-3xl p-5 md:p-8"
              style={{
                background: PALETTE[grouped.findIndex((g) => g.id === active.id) % PALETTE.length].bg,
                color: PALETTE[grouped.findIndex((g) => g.id === active.id) % PALETTE.length].text,
              }}
            >
              {/* Background blobs */}
              <motion.div
                className="absolute -top-10 -right-10 w-44 h-44 rounded-full pointer-events-none"
                style={{ background: "rgba(255,255,255,0.18)" }}
                animate={{ scale: [1, 1.15, 1], rotate: [0, 18, 0] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -bottom-12 -left-10 w-44 h-44 rounded-full pointer-events-none"
                style={{ background: "rgba(255,255,255,0.1)" }}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />

              {/* Close */}
              <button
                onClick={() => setActiveCatId(null)}
                className="absolute top-3.5 right-3.5 z-20 w-11 h-11 rounded-full flex items-center justify-center bg-white/20 hover:bg-white/35 active:scale-95 transition-all"
                data-testid="treemap-close"
                aria-label="Chiudi"
              >
                <X className="w-6 h-6" strokeWidth={2.4} />
              </button>

              <div className="relative">
                <div className="flex items-center gap-3 mb-2 pr-12">
                  <CategoryIcon catId={active.id} size={28} />
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-75">{active.items.length} {t("pl.amenities.items_many")}</p>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-5 md:mb-7" style={{ letterSpacing: "-0.02em" }}>{catLabel(active.id, active.label)}</h3>

                {/* Items list with stagger */}
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={{ visible: { transition: { staggerChildren: 0.035, delayChildren: 0.08 } } }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full"
                  data-testid="treemap-expanded-items"
                >
                  {active.items.map((it, i) => (
                    <motion.div
                      key={it.id}
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                      transition={{ type: "spring", damping: 22, stiffness: 240, mass: 0.6 }}
                      className="rounded-2xl px-3.5 py-2.5 md:px-4 md:py-3 flex items-center gap-3 text-sm md:text-[15px] font-semibold min-w-0"
                      style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}
                    >
                      {it.icon ? <span className="text-lg shrink-0">{it.icon}</span> : <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "rgba(255,255,255,0.5)" }} />}
                      <div className="flex-1 min-w-0">
                        <p className="break-words">{amenityLabel(it.id, it.label, lang)}</p>
                        {it.desc && <p className="text-xs opacity-80 mt-0.5 line-clamp-2">{amenityDesc(it.id, it.desc, lang)}</p>}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AmenitiesTreemap;
